<template>
  <div>
    <v-dialog
      v-if="$vuetify.breakpoint.mdAndUp"
      v-model="value"
      max-width="600px"
      width="85%"
      @click:outside="$emit('input', false)"
    >
      <v-card>
        <v-container class="no-gutters pa-0">
          <v-row class="no-gutters primary pa-3">
            <v-col
              class="col-4 no-gutters justify-center align-center contact-flex"
            >
              <v-avatar
                :color="profileImageUrl ? '' : 'primary'"
                class="darken-2 contact-avatar"
                size="100"
              >
                <img
                  v-if="profileImageUrl"
                  :src="profileImageUrl"
                  alt="profile picture"
                  class="contact-image"
                  @error="imageAltUrl"
                >
                <span
                  v-else
                  class="white--text sn-display-3 sn-font-weight-medium sn-font-standard"
                >
                  {{ person.initials }}
                </span>
              </v-avatar>
            </v-col>
            <v-col
              class="col-8 no-gutters justify-end contact-flex"
            >
              <v-row class="justify-end no-gutters">
                <v-btn
                  icon
                  dark
                  aria-label="close"
                  @click.native="$emit('input', false)"
                >
                  <v-icon>sn-icon-exit</v-icon>
                </v-btn>
              </v-row>
              <h1
                :class="headerClass"
                class="white--text"
              >
                {{ person.full_name }}
              </h1>
              <p class="sn-subtitle-2 white--text mb-0">
                {{ person.title }}
                <span v-if="formattedNMLS">
                  | {{ formattedNMLS }}
                </span>
              </p>
            </v-col>
          </v-row>
          <v-row class="no-gutters pa-3">
            <v-col class="col-4 no-gutters" />
            <v-col class="col-8 no-gutters sn-font-primary">
              <!-- main and office phone -->
              <p
                v-if="person.unformatted_phone"
                class="mb-0"
              >
                <span>M: </span>
                <a
                  :href="'tel:+1' + person.unformatted_phone"
                  class="no-underline"
                  aria-label="Main Phone"
                >
                  {{ person.phone }}
                </a>
              </p>
              <p
                v-if="person.unformatted_office_phone"
                class="mb-0"
              >
                <span>O: </span>
                <a
                  :href="'tel:+1' + person.unformatted_office_phone"
                  class="no-underline"
                  aria-label="Office Phone"
                >
                  {{ person.office_phone }}
                </a>
              </p>
              <!-- email and website -->
              <v-spacer
                v-if="person.unformatted_phone || person.unformatted_office_phone"
                class="mb-3"
              />
              <p
                v-if="person.email"
                class="mb-0"
              >
                <a
                  :href="'mailto:' + person.email"
                  class="no-underline"
                  aria-label="Email"
                >
                  {{ person.email }}
                </a>
              </p>
              <p
                v-if="person.website"
                class="mb-0"
              >
                <a
                  :href="person.website"
                  class="no-underline"
                  target="_blank"
                  aria-label="Website"
                >
                  {{ person.website }}
                </a>
              </p>
              <v-spacer
                v-if="person.email || person.website"
                class="mb-3"
              />
              <!-- address line 1 and 2 -->
              <p
                v-if="addressLineOne"
                class="mb-0"
              >
                {{ addressLineOne }}
              </p>
              <p
                v-if="addressLineTwo"
                class="mb-0"
              >
                {{ addressLineTwo }}
              </p>
              <v-spacer
                v-if="addressLineOne || addressLineTwo"
                class="mb-3"
              />
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>
    <!-- For small and down screens, this full screen dialog appears instead of the "business card" popup -->
    <v-dialog
      v-else
      v-model="value"
      :overlay="false"
      fullscreen
      transition="dialog-bottom-transition"
    >
      <v-card>
        <v-toolbar
          dark
          color="primary"
          style="height:60px;"
        >
          <v-btn
            icon
            dark
            aria-label="close"
            @click.native="$emit('input', false)"
          >
            <v-icon>sn-icon-exit</v-icon>
          </v-btn>
          <v-toolbar-title>{{ person.full_name }}</v-toolbar-title>
        </v-toolbar>
        <v-card-text>
          <v-row>
            <v-col
              cols="2"
            >
              <v-avatar
                size="60"
                color="primary"
              >
                <img
                  v-if="profileImageUrl"
                  :src="profileImageUrl"
                  alt="profile picture"
                  @error="imageAltUrl"
                >
                <span
                  v-else
                  class="white--text sn-display-3 sn-font-weight-medium sn-font-standard"
                >
                  {{ person.initials }}
                </span>
              </v-avatar>
            </v-col>
            <v-col
              style="margin-left: 24px;"
              cols="9"
            >
              <h5 class="headline headline mb-1">
                {{ person.full_name }}
              </h5>
              <h2 class="subheading">
                {{ person.title || 'Loan Officer' }}
              </h2>
            </v-col>
          </v-row>
          <v-layout
            row
            class="mt-2"
          >
            <v-flex xs12>
              <p class="title mb-1">
                Contact
              </p>
              <p class="subheading mb-1">
                <a :href="'tel:+1' + person.unformatted_phone">{{ person.phone }}</a>
              </p>
              <p class="subheading mb-1">
                <a :href="'mailto:' + person.email">{{ person.email }}</a>
              </p>
              <p>
                {{ addressLineOne }}
                <br>
                {{ addressLineTwo }}
              </p>
              <p class="title mb-1 mt-4">
                Resources
              </p>
              <p class="subheading mb-1">
                <a :href="person.website">
                  My website
                </a>
              </p>
              <p
                v-for="link in person.effective_web_links"
                :key="link.id"
                class="subheading mb-1"
              >
                <a
                  :href="link.url"
                  target="_blank"
                >
                  {{ getWebLinkText(link) }}
                </a>
              </p>
              <p
                v-if="formattedNMLS"
                class="subheading mb-1"
              >
                {{ formattedNMLS }}
              </p>
            </v-flex>
          </v-layout>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { formatNMLS } from '../Shared/formatNMLS.js'

export default {
  name: 'ContactInfoDialog',
  props: {
    person: {
      type: Object,
      default: () => {},
      required: true
    },
    value: {
      type: Boolean,
      default: false,
      required: true
    }
  },
  computed: {
    profileImageUrl () {
      return this.person?.profile_image_url
    },
    formattedNMLS () {
      return formatNMLS(this.person?.license)
    },
    addressLineOne () {
      let firstLine = ''
      if (this.person.street1) firstLine += this.person.street1
      if (this.person.street2) firstLine += ', ' + this.person.street2
      return firstLine
    },
    addressLineTwo () {
      let secondLine = ''
      if (this.person.city) secondLine += this.person.city + ', '
      if (this.person.state) secondLine += this.person.state
      if (this.person.zip) secondLine += ' ' + this.person.zip
      return secondLine
    },
    headerClass () {
      switch (true) {
        case this.person.full_name.length < 16:
          return 'sn-h1'
        case this.person.full_name.length < 20:
          return 'sn-h2'
        case this.person.full_name.length < 24:
          return 'sn-h3'
        case this.person.full_name.length < 28:
          return 'sn-h4'
        default:
          return 'sn-h5'
      }
    }
  },
  methods: {
    imageAltUrl (event) {
      event.target.src = 'https://simplenexus.com/images/profile_sihlouette.png'
    },
    getWebLinkText (link) {
      return link.link_type ? link.link_type.name : link.link_text || 'Web Link'
    }
  }
}
</script>
<style scoped>
  .no-underline {
    text-decoration: none;
  }
  .contact-avatar {
    top: 60%;
    min-height: 48px;
  }
  .contact-flex {
    display: flex;
    flex-direction: column;
  }
</style>
